import React, { Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '../lib/supabase_client';

function safeJsonParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function safeTruncate(value: string | null | undefined, maxLen: number): string | null {
  if (!value) return null;
  if (value.length <= maxLen) return value;
  return value.slice(0, maxLen) + '…[truncated]';
}

function makeEventId(): string {
  const anyCrypto = crypto as any;
  if (anyCrypto?.randomUUID) return anyCrypto.randomUUID();
  return `evt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: undefined };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack || undefined });

    // ✅ simplest: save the crash into Supabase (best-effort, never throws)
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const url = typeof window !== 'undefined' ? window.location.href : '';

      // We only wrap Edit Filings with this boundary, so parse IDs from URL directly.
      const m = path.match(/^\/all-filings\/([^/]+)\/edit\/([^/?#]+)/);
      const companyIdFromUrl = m?.[1] ?? null;
      const filingIdFromUrl = m?.[2] ?? null;

      // Persisted org selection is already stored by CompanyContext
      const selectedCompany = safeJsonParse<{ company_id?: string; company_name?: string }>(
        localStorage.getItem('selectedCompany')
      );

      const orgId = selectedCompany?.company_id ?? companyIdFromUrl;
      const orgName = selectedCompany?.company_name ?? null;

      const eventId = makeEventId();
      const fingerprint = `${error?.name || ''}|${error?.message || ''}|${safeTruncate(error?.stack, 500) || ''}|${path}`;

      // Prevent spamming the same crash repeatedly in one tab/session.
      const lastFp = sessionStorage.getItem('last_app_crash_fingerprint');
      if (lastFp !== fingerprint) {
        sessionStorage.setItem('last_app_crash_fingerprint', fingerprint);

        void (async () => {
          try {
            const { data } = await supabase.auth.getSession();
            const user = data?.session?.user ?? null;

            await supabase.from('client_error_reports').insert([
              {
                event_id: eventId,
                occurred_at: new Date().toISOString(),
                url,
                path,
                page: 'edit_filings',
                company_id: companyIdFromUrl,
                filing_id: filingIdFromUrl,
                org_id: orgId,
                org_name: orgName,
                user_id: user?.id ?? null,
                user_email: user?.email ?? null,
                error_name: safeTruncate(error?.name, 200),
                error_message: safeTruncate(error?.message, 2000),
                error_stack: safeTruncate(error?.stack, 20000),
                component_stack: safeTruncate(errorInfo?.componentStack || undefined, 20000),
                meta: {
                  user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                  // keep the same timestamp in meta for quick display
                  at: new Date().toISOString(),
                  // Extract bundle version from loaded scripts (e.g., main.ccedc8f1.js -> ccedc8f1)
                  bundle_version: (() => {
                    try {
                      const scripts = document.querySelectorAll('script[src*="/static/js/main."]');
                      if (scripts.length > 0) {
                        const src = (scripts[0] as HTMLScriptElement).src;
                        const match = src.match(/main\.([a-f0-9]+)\.js/);
                        return match ? match[1] : null;
                      }
                      return null;
                    } catch {
                      return null;
                    }
                  })(),
                },
              },
            ]);
          } catch {
            // ignore
          }
        })();
      }
    } catch {
      // ignore
    }

    try {
      // Persist the last crash so you can inspect after refresh
      sessionStorage.setItem(
        'last_app_crash',
        JSON.stringify({
          message: error?.message,
          stack: error?.stack,
          componentStack: errorInfo?.componentStack || undefined,
          at: new Date().toISOString(),
        })
      );
    } catch {
      // ignore
    }
  }

  render() {
    if (this.state.hasError) {
      let persisted: any = null;
      try {
        persisted = JSON.parse(sessionStorage.getItem('last_app_crash') || 'null');
      } catch {
        persisted = null;
      }

      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '500px',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            padding: '32px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px'
            }}>
              Something went wrong
            </h2>
            <p style={{
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              The page encountered an error. This might be because your session expired or there was a temporary issue.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Reload Page
              </button>
              <button
                onClick={() => window.history.back()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Go Back
              </button>
            </div>

            <details style={{ marginTop: '16px', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', color: '#374151', fontWeight: 600 }}>
                Show error details
              </summary>
              <pre style={{
                marginTop: '10px',
                padding: '12px',
                background: '#f3f4f6',
                borderRadius: '8px',
                overflow: 'auto',
                fontSize: '12px',
                lineHeight: 1.4,
                color: '#111827',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
{`message: ${(this.state.error?.message || persisted?.message || 'unknown')}

stack:
${this.state.error?.stack || persisted?.stack || 'n/a'}

componentStack:
${this.state.componentStack || persisted?.componentStack || 'n/a'}`}
              </pre>
              <button
                onClick={() => {
                  const text = `message: ${(this.state.error?.message || persisted?.message || 'unknown')}\n\nstack:\n${this.state.error?.stack || persisted?.stack || 'n/a'}\n\ncomponentStack:\n${this.state.componentStack || persisted?.componentStack || 'n/a'}`;
                  navigator.clipboard?.writeText?.(text);
                }}
                style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '12px'
                }}
              >
                Copy error details
              </button>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
